import * as constants from '../../../constants/constants'
import type {DeserializeComponentParams} from './types'
import {updateComponentPropsStructure} from './namespaces/props'
import {updateComponentMobileHintsStructure} from './namespaces/mobileHints'
import {updateComponentDataStructure} from './namespaces/data'
import {updateComponentAnchorStructure} from './namespaces/anchors'
import {updateComponentConnectionsStructure} from './namespaces/connections'
import {updateComponentControllersStructure} from './namespaces/controllers'
import {updateComponentPatternsStructure} from './namespaces/patterns'
import {updateComponentBehaviorsStructure} from './namespaces/behaviors'
import {updateComponentStylesStructure} from './namespaces/style'
import {updateComponentLayoutStructure} from './namespaces/layout'
import {updateComponentDesignStructure} from './namespaces/design'
import {updateComponentReactionsStructure} from './namespaces/reactions'
import {updateComponentTransitionsStructure} from './namespaces/transitions'
import {updateComponentTransformationsStructure} from './namespaces/transformations'
import {FEATURES_DATA_TYPE, updateComponentFeaturesStructure} from './namespaces/features'
import {updateComponentEffectsStructure} from './namespaces/effects'
import {updateComponentStatesStructure, updateComponentTriggersStructure} from './namespaces/statesTriggers'
import {deserializeInnerElements} from './namespaces/innerElements'
import type {CreateExtArgs} from '@wix/document-manager-core'
import type {DeserializationMappers, SerializedCompStructure} from '@wix/document-services-types'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import _ from 'lodash'
import {addIdToMap} from './utils'

const {DATA_TYPES} = constants

type DeserializeComponentImplementation = (deserializationParams: DeserializeComponentParams) => void

// Ordering is important, some namespaces have references to others. deserialization MUST occur in a particular order!
export const namespaceMapping: Map<string, DeserializeComponentImplementation> = new Map([
    [DATA_TYPES.innerElements, deserializeInnerElements],
    [DATA_TYPES.data, updateComponentDataStructure],
    [DATA_TYPES.prop, updateComponentPropsStructure],
    [DATA_TYPES.mobileHints, updateComponentMobileHintsStructure],
    [DATA_TYPES.anchors, updateComponentAnchorStructure],
    [DATA_TYPES.connections, updateComponentConnectionsStructure],
    [DATA_TYPES.controllers, updateComponentControllersStructure],
    [DATA_TYPES.patterns, updateComponentPatternsStructure],
    [DATA_TYPES.theme, updateComponentStylesStructure],
    [DATA_TYPES.layout, updateComponentLayoutStructure],
    [DATA_TYPES.design, updateComponentDesignStructure],
    [DATA_TYPES.transitions, updateComponentTransitionsStructure],
    [DATA_TYPES.effects, updateComponentEffectsStructure],
    [DATA_TYPES.reactions, updateComponentReactionsStructure],
    [DATA_TYPES.transformations, updateComponentTransformationsStructure],
    [FEATURES_DATA_TYPE, updateComponentFeaturesStructure],
    [DATA_TYPES.behaviors, updateComponentBehaviorsStructure],
    [DATA_TYPES.states, updateComponentStatesStructure],
    [DATA_TYPES.triggers, updateComponentTriggersStructure]
])

export const deserializeComponentData = (deserializationParams: DeserializeComponentParams) => {
    namespaceMapping.forEach(updateNamespaceImpl => {
        updateNamespaceImpl(deserializationParams)
    })
}

export const addInternallyReferredDataItemsToMap = (
    createExtArgs: CreateExtArgs,
    compStructure: SerializedCompStructure,
    mappers?: DeserializationMappers
) => {
    const {internallyReferredDataItems} = compStructure

    if (_.isEmpty(internallyReferredDataItems)) {
        return
    }

    const {extensionAPI} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    if (!mappers?.oldToNewIdMap) {
        return
    }

    const {oldToNewIdMap} = mappers

    for (const [dataType, pointers] of Object.entries(internallyReferredDataItems)) {
        for (const {id} of pointers) {
            if (oldToNewIdMap[id]) {
                continue
            }

            addIdToMap({id}, dataModel.generateNewId(dataType), oldToNewIdMap)
        }
    }
}
